import { useState, useCallback, useEffect, useRef } from 'react';
import { w3cwebsocket as W3CWebSocket } from 'websocket';
import { ChatAgentType } from '@constants/chat-agents';
import { connectChatWebSocket, sendChatMessage, sendForwardMessage } from '@services/chatService';
import type { ChatMessage } from '../types';
import type { ChatHistoryResponse, ForwardedImage } from '../types/chat-message';
import type { ChatComponentItem } from '../types/chat-component';

export enum ConnectionStatus {
  CONNECTED = 'connected',
  CONNECTING = 'connecting',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
}

interface UseChatWebSocketReturn {
  sockets: Record<ChatAgentType, W3CWebSocket | null>;
  conversationIds: Record<ChatAgentType, number | null>;
  isFirstConnections: Record<ChatAgentType, boolean>;
  connectionStatuses: Record<ChatAgentType, ConnectionStatus>;
  needsManualReconnect: Record<ChatAgentType, boolean>;
  connectWebSocket: (agentType: ChatAgentType, conversationId: number, token: string, loadHistory?: boolean) => void;
  manualReconnect: (agentType: ChatAgentType) => void;
  sendMessage: (
    agentType: ChatAgentType,
    message: string,
    buttonIdOrOnError?: number | ((error: Error) => void),
    onError?: (error: Error) => void,
    isHidden?: boolean,
    replyToMessageId?: string | number,
    replyToCodeMessage?: string,
    codeMessage?: string,
    images?: Array<{ contentType: string; imageUrl: string; name: string; uploadStatus: string }>,
    components?: ChatComponentItem[],
  ) => boolean;
  sendForwardMessage: (
    agentType: ChatAgentType,
    typeChangeFlow: string,
    targetAgent: ChatAgentType,
    msgUserFwToNewAgent: string | undefined,
    msgBotFwToNewAgentId: string | undefined,
    msgUserFwCodeMessage: string | undefined,
    onError?: (error: Error) => void,
    msgUserImagesFw?: ForwardedImage[],
    sessionId?: string | null,
  ) => boolean;
  setConversationId: (agentType: ChatAgentType, conversationId: number | null) => void;
  disconnectOtherSockets: (exceptAgent: ChatAgentType) => void;
  disconnectWebSocket: (agentType: ChatAgentType) => void;
  clearQueue: (agentType: ChatAgentType) => void;
  setUserLocation: (location: string) => void;
}

const initialSockets: Record<ChatAgentType, W3CWebSocket | null> = {
  [ChatAgentType.AI]: null,
  [ChatAgentType.CSKH]: null,
  [ChatAgentType.TAX_QUESTION]: null,
  [ChatAgentType.INSTRUCTION_MANUAL]: null,
  [ChatAgentType.ANALYTICS_QUESTION]: null,
};

const initialConversationIds: Record<ChatAgentType, number | null> = {
  [ChatAgentType.AI]: null,
  [ChatAgentType.CSKH]: null,
  [ChatAgentType.TAX_QUESTION]: null,
  [ChatAgentType.INSTRUCTION_MANUAL]: null,
  [ChatAgentType.ANALYTICS_QUESTION]: null,
};

const initialIsFirstConnections: Record<ChatAgentType, boolean> = {
  [ChatAgentType.AI]: true,
  [ChatAgentType.CSKH]: true,
  [ChatAgentType.TAX_QUESTION]: true,
  [ChatAgentType.INSTRUCTION_MANUAL]: true,
  [ChatAgentType.ANALYTICS_QUESTION]: true,
};

const initialConnectionStatuses: Record<ChatAgentType, ConnectionStatus> = {
  [ChatAgentType.AI]: ConnectionStatus.DISCONNECTED,
  [ChatAgentType.CSKH]: ConnectionStatus.DISCONNECTED,
  [ChatAgentType.TAX_QUESTION]: ConnectionStatus.DISCONNECTED,
  [ChatAgentType.INSTRUCTION_MANUAL]: ConnectionStatus.DISCONNECTED,
  [ChatAgentType.ANALYTICS_QUESTION]: ConnectionStatus.DISCONNECTED,
};

const initialNeedsManualReconnect: Record<ChatAgentType, boolean> = {
  [ChatAgentType.AI]: false,
  [ChatAgentType.CSKH]: false,
  [ChatAgentType.TAX_QUESTION]: false,
  [ChatAgentType.INSTRUCTION_MANUAL]: false,
  [ChatAgentType.ANALYTICS_QUESTION]: false,
};

const MAX_AUTO_RECONNECT_ATTEMPTS = 5;

export const useChatWebSocket = (
  onMessage: (agentType: ChatAgentType, chatMessage: ChatMessage) => void,
  onHistoryLoaded: (agentType: ChatAgentType, history: ChatHistoryResponse) => void,
  onError?: (agentType: ChatAgentType, error: Error) => void,
  onQueuingMessage?: (agentType: ChatAgentType, codeMessage: string) => void,
  onClose?: (agentType: ChatAgentType) => void
) => {
  const [sockets, setSockets] = useState<Record<ChatAgentType, W3CWebSocket | null>>(
    initialSockets
  );
  const [conversationIds, setConversationIds] = useState<Record<ChatAgentType, number | null>>(
    initialConversationIds
  );
  const [isFirstConnections, setIsFirstConnections] = useState<Record<ChatAgentType, boolean>>(
    initialIsFirstConnections
  );
  const [connectionStatuses, setConnectionStatuses] = useState<Record<ChatAgentType, ConnectionStatus>>(
    initialConnectionStatuses
  );
  const [needsManualReconnect, setNeedsManualReconnect] = useState<Record<ChatAgentType, boolean>>(
    initialNeedsManualReconnect
  );

  // Use Ref to always have the most current socket instance in logic
  const socketRefs = useRef<Record<ChatAgentType, W3CWebSocket | null>>({ ...initialSockets });
  const reconnectAttemptsRef = useRef<Record<ChatAgentType, number>>({
    [ChatAgentType.AI]: 0,
    [ChatAgentType.CSKH]: 0,
    [ChatAgentType.TAX_QUESTION]: 0,
    [ChatAgentType.INSTRUCTION_MANUAL]: 0,
    [ChatAgentType.ANALYTICS_QUESTION]: 0,
  });

  // Keep track of the current token and conversationId for reconnecting
  const connectionDetailsRef = useRef<Record<ChatAgentType, { conversationId: number; token: string } | null>>({
    [ChatAgentType.AI]: null,
    [ChatAgentType.CSKH]: null,
    [ChatAgentType.TAX_QUESTION]: null,
    [ChatAgentType.INSTRUCTION_MANUAL]: null,
    [ChatAgentType.ANALYTICS_QUESTION]: null,
  });

  // Use Ref to store message queue instead of single message
  const pendingMessagesRef = useRef<Record<ChatAgentType, Array<{
    message: string;
    buttonId?: number;
    isHidden?: boolean;
    replyToMessageId?: string | number;
    replyToCodeMessage?: string;
    codeMessage?: string;
    images?: Array<{ contentType: string; imageUrl: string; name: string; uploadStatus: string }>;
    components?: ChatComponentItem[];
    errorCallback?: (error: Error) => void;
  }>>>({
    [ChatAgentType.AI]: [],
    [ChatAgentType.CSKH]: [],
    [ChatAgentType.TAX_QUESTION]: [],
    [ChatAgentType.INSTRUCTION_MANUAL]: [],
    [ChatAgentType.ANALYTICS_QUESTION]: [],
  });

  const locationRef = useRef('');
  const setUserLocation = (location: string) => { locationRef.current = location; };

  // Ref to store onMessage callback (to avoid stale closure during reconnect)
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Ref to store onQueuingMessage callback (to avoid stale closure)
  const onQueuingMessageRef = useRef(onQueuingMessage);
  useEffect(() => {
    onQueuingMessageRef.current = onQueuingMessage;
  }, [onQueuingMessage]);

  // Ref to store onClose callback (to avoid stale closure)
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const reconnectTimeoutRefs = useRef<Record<ChatAgentType, ReturnType<typeof setTimeout> | null>>({
    [ChatAgentType.AI]: null,
    [ChatAgentType.CSKH]: null,
    [ChatAgentType.TAX_QUESTION]: null,
    [ChatAgentType.INSTRUCTION_MANUAL]: null,
    [ChatAgentType.ANALYTICS_QUESTION]: null,
  });

  const connectWebSocket = useCallback(
    (agentType: ChatAgentType, conversationId: number, token: string, isManual: boolean = false, loadHistory: boolean = false) => {
      // Store connection details for potential reconnects (even if we skip first attempt due to offline)
      connectionDetailsRef.current[agentType] = { conversationId, token };

      // Reset attempts and flag whenever we initiate a connection attempt
      reconnectAttemptsRef.current[agentType] = 0;
      setNeedsManualReconnect(prev => ({ ...prev, [agentType]: false }));

      if (reconnectTimeoutRefs.current[agentType]) {
        clearTimeout(reconnectTimeoutRefs.current[agentType]!);
        reconnectTimeoutRefs.current[agentType] = null;
      }

      // Check offline status
      if (!window.navigator.onLine) {
        console.warn(`[Socket] Browser is offline. Delaying connection for ${agentType}`);
        setConnectionStatuses(prev => ({ ...prev, [agentType]: ConnectionStatus.ERROR }));
        setNeedsManualReconnect(prev => ({ ...prev, [agentType]: true }));
        return;
      }

      // 1. Cleanup old socket for this agent
      const currentSocket = socketRefs.current[agentType];
      if (currentSocket) {
        console.log(`[Socket] Closing old socket for ${agentType} before reconnecting`);
        currentSocket.onclose = () => { }; // Remove handler to prevent trigger handleClose
        currentSocket.onerror = () => { };
        currentSocket.onmessage = () => { }; // Prevent obsolete messages
        currentSocket.close();
      }

      // 2. Create new socket
      console.log(`[Socket] Connecting for ${agentType}...`);
      setConnectionStatuses(prev => ({ ...prev, [agentType]: ConnectionStatus.CONNECTING }));

      const newSocket = connectChatWebSocket(
        conversationId,
        token,
        (chatMessage: ChatMessage) => {
          if (onMessageRef.current) {
            const targetAgent = Object.values(ChatAgentType).includes(chatMessage.agentType as ChatAgentType)
              ? (chatMessage.agentType as ChatAgentType)
              : agentType;
            onMessageRef.current(targetAgent, chatMessage);
          }
        },
        (history: ChatHistoryResponse) => {
          onHistoryLoaded(agentType, history);
          setIsFirstConnections((prev) => ({
            ...prev,
            [agentType]: false,
          }));
        },
        (error: Error) => {
          console.error(`WebSocket connection error for ${agentType}:`, error);
          setConnectionStatuses(prev => ({ ...prev, [agentType]: ConnectionStatus.ERROR }));
          if (isFirstConnections[agentType]) {
            setIsFirstConnections((prev) => ({
              ...prev,
              [agentType]: false,
            }));
          }
          onError?.(agentType, error);
        },
        () => {
          // onOpen
          console.log(`[Socket] Connected for ${agentType}`);
          setConnectionStatuses(prev => ({ ...prev, [agentType]: ConnectionStatus.CONNECTED }));
          reconnectAttemptsRef.current[agentType] = 0;
          setNeedsManualReconnect(prev => ({ ...prev, [agentType]: false }));

          // Send all pending messages in queue (only via socket, do NOT add to UI)
          const queue = pendingMessagesRef.current[agentType];
          if (queue.length > 0) {
            console.log(`[Socket] Sending ${queue.length} queuing messages for ${agentType} (no UI push)`);
            while (queue.length > 0) {
              const pending = queue.shift();
              if (pending) {
                sendChatMessage(
                  newSocket,
                  pending.message,
                  conversationId,
                  pending.errorCallback,
                  agentType,
                  pending.buttonId,
                  pending.isHidden ?? false,
                  pending.replyToMessageId,
                  pending.replyToCodeMessage,
                  pending.codeMessage,
                  pending.images,
                  locationRef.current,
                  pending.components,
                );
              }
            }
          }
        },
        loadHistory, // Load history only when explicitly requested (e.g. forward agent)
        agentType
      );

      // 3. Setup core handlers immediately
      newSocket.onclose = (event: any) => {
        handleClose(agentType, newSocket, event, conversationId);
      };

      // 4. Update Ref and State
      socketRefs.current[agentType] = newSocket;
      setSockets((prev) => ({ ...prev, [agentType]: newSocket }));

      return newSocket;
    },
    [onMessage, onHistoryLoaded, onError]
  );

  const handleClose = (agentType: ChatAgentType, socket: W3CWebSocket, event: any, conversationId: number) => {
    // Only handle if this socket is still the active one
    if (socketRefs.current[agentType] !== socket) {
      console.log(`[Socket] Ignored onclose for obsolete ${agentType} socket`);
      return;
    }

    console.warn(`WebSocket disconnected for ${agentType}:`, event.code, event.reason);

    // Clear from current active tracking
    socketRefs.current[agentType] = null;
    setSockets((prev) => ({ ...prev, [agentType]: null }));
    setConnectionStatuses(prev => ({ ...prev, [agentType]: ConnectionStatus.DISCONNECTED }));

    if (onCloseRef.current) {
      onCloseRef.current(agentType);
    }

    // Reconnect logic
    const token = sessionStorage.getItem('access_token');
    if (conversationId && token && event && !event.wasClean) {
      // Check if we already reached max attempts or is offline
      if (reconnectAttemptsRef.current[agentType] >= MAX_AUTO_RECONNECT_ATTEMPTS || !window.navigator.onLine) {
        console.log(`[Socket] Reconnect limit reached or offline for ${agentType}. Manual reconnect required.`);
        // Khi phải reconnect manual, ta xóa các tin nhắn cũ nếu người dùng không đang nhắn tin
        // Nhưng ở đây ta giữ lại tin vừa bắn fail để chờ reconnect manual xong sẽ gửi (theo yêu cầu user)
        setNeedsManualReconnect(prev => ({ ...prev, [agentType]: true }));
        return;
      }

      reconnectAttemptsRef.current[agentType] += 1;
      const delay = Math.min(3000 * reconnectAttemptsRef.current[agentType], 15000); // Exponential-ish backoff

      console.log(`[Socket] Unintentional disconnect for ${agentType}. Attempting to reconnect (Attempt ${reconnectAttemptsRef.current[agentType]}) in ${delay / 1000}s...`);

      if (reconnectTimeoutRefs.current[agentType]) {
        clearTimeout(reconnectTimeoutRefs.current[agentType]!);
      }

      reconnectTimeoutRefs.current[agentType] = setTimeout(() => {
        // Double check if conversation hasn't changed
        if (conversationIds[agentType] === conversationId || !conversationIds[agentType]) {
          connectWebSocket(agentType, conversationId, token);
        }
        reconnectTimeoutRefs.current[agentType] = null;
      }, delay);
    }
  };

  const manualReconnect = useCallback((agentType: ChatAgentType) => {
    const details = connectionDetailsRef.current[agentType];
    const token = sessionStorage.getItem('access_token');
    if (details && (token || details.token)) {
      console.log(`[Socket] Manual reconnect triggered for ${agentType}`);
      connectWebSocket(agentType, details.conversationId, token || details.token, true);
    } else {
      console.warn(`[Socket] Cannot manual reconnect for ${agentType}: missing details`);
    }
  }, [connectWebSocket]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log(`[Socket] Browser is back online`);
      // Optionally trigger reconnect for agents that need it
      Object.keys(needsManualReconnect).forEach((key) => {
        const agentType = key as ChatAgentType;
        if (needsManualReconnect[agentType]) {
          // You might want to auto-retry once on back online
          // manualReconnect(agentType);
        }
      });
    };

    const handleOffline = () => {
      console.log(`[Socket] Browser went offline`);
      Object.keys(socketRefs.current).forEach((key) => {
        const agentType = key as ChatAgentType;
        if (socketRefs.current[agentType]) {
          // If already connected, it will trigger handleClose when it times out or heartbeat fails
          // But we can proactively set manual reconnect flag if we know we are offline
          setNeedsManualReconnect(prev => ({ ...prev, [agentType]: true }));
        }
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [needsManualReconnect, manualReconnect]);

  const sendMessage = useCallback(
    (
      agentType: ChatAgentType,
      message: string,
      buttonIdOrOnError?: number | ((error: Error) => void),
      onError?: (error: Error) => void,
      isHidden?: boolean,
      replyToMessageId?: string | number,
      replyToCodeMessage?: string,
      codeMessage?: string,
      images?: Array<{ contentType: string; imageUrl: string; name: string; uploadStatus: string }>,
      components?: ChatComponentItem[],
    ): boolean => {
      // Use Ref to get most recent socket status
      const socket = socketRefs.current[agentType];
      const conversationId = conversationIds[agentType];

      let buttonId: number | undefined;
      let errorCallback: ((error: Error) => void) | undefined;

      if (typeof buttonIdOrOnError === 'number') {
        buttonId = buttonIdOrOnError;
        errorCallback = onError;
      } else {
        errorCallback = buttonIdOrOnError;
      }

      if (!socket || socket.readyState !== WebSocket.OPEN) {
        console.warn(`[Socket] ${agentType} not ready (state: ${socket?.readyState}), queuing message`);

        // Only show manual reconnect UI if the socket is CLOSED or CLOSING (not CONNECTING)
        if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
          setNeedsManualReconnect(prev => ({ ...prev, [agentType]: true }));
        }

        // Đè tin nhắn mới nhất vào hàng chờ (chỉ giữ 1 tin duy nhất để gửi khi reconnect)
        pendingMessagesRef.current[agentType] = [{
          message,
          buttonId,
          isHidden,
          replyToMessageId,
          replyToCodeMessage,
          codeMessage,
          images,
          components,
          errorCallback
        }];

        // Notify UI that this message is queuing (so UI can mark it)
        if (codeMessage && onQueuingMessageRef.current) {
          onQueuingMessageRef.current(agentType, codeMessage);
        }

        // Attempt reconnection immediately if closed/error and not already attempting
        const token = sessionStorage.getItem('access_token');
        if (token && conversationId) {
          if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
            // We still try to connect in background, but the UI will now show the manual button
            connectWebSocket(agentType, conversationId, token);
          }
        }
        return true;
      }

      if (!conversationId) {
        errorCallback?.(new Error('Conversation ID not available'));
        return false;
      }

      return sendChatMessage(socket, message, conversationId, errorCallback, agentType, buttonId, isHidden ?? false, replyToMessageId, replyToCodeMessage, codeMessage, images, locationRef.current, components);
    },
    [conversationIds, connectWebSocket]
  );

  const sendForwardMessageToSocket = useCallback(
    (
      agentType: ChatAgentType,
      typeChangeFlow: string,
      targetAgent: ChatAgentType,
      msgUserFwToNewAgent: string | undefined,
      msgBotFwToNewAgentId: string | undefined,
      msgUserFwCodeMessage: string | undefined,
      onErrorCb?: (error: Error) => void,
      msgUserImagesFw?: ForwardedImage[],
      sessionId?: string | null,
    ): boolean => {
      const socket = socketRefs.current[agentType];
      const conversationId = conversationIds[agentType];

      if (!socket || !conversationId) {
        onErrorCb?.(new Error('WebSocket or conversation ID not available'));
        return false;
      }

      return sendForwardMessage(
        socket,
        conversationId,
        typeChangeFlow,
        targetAgent,
        msgUserFwToNewAgent,
        msgBotFwToNewAgentId,
        msgUserFwCodeMessage,
        onErrorCb,
        agentType,
        msgUserImagesFw,
        sessionId,
      );
    },
    [conversationIds]
  );

  const setConversationId = useCallback((agentType: ChatAgentType, conversationId: number | null) => {
    setConversationIds((prev) => ({
      ...prev,
      [agentType]: conversationId,
    }));
  }, []);

  const disconnectOtherSockets = useCallback((exceptAgent: ChatAgentType) => {
    Object.keys(socketRefs.current).forEach((key) => {
      const agentType = key as ChatAgentType;
      const socket = socketRefs.current[agentType];
      if (socket && agentType !== exceptAgent) {
        console.log(`[Socket] Disconnecting socket for ${agentType}`);
        socket.onclose = () => { };
        socket.onerror = () => { };
        socket.onmessage = () => { };
        socket.close();
        socketRefs.current[agentType] = null;
        setSockets((prev) => ({ ...prev, [agentType]: null }));
      }
    });
  }, []);

  const disconnectWebSocket = useCallback((agentType: ChatAgentType) => {
    const socket = socketRefs.current[agentType];
    if (socket) {
      console.log(`[Socket] Disconnecting socket for ${agentType}`);
      socket.onclose = () => { };
      socket.onerror = () => { };
      socket.onmessage = () => { };
      socket.close();
      socketRefs.current[agentType] = null;
      setSockets((prev) => ({ ...prev, [agentType]: null }));
      setConnectionStatuses(prev => ({ ...prev, [agentType]: ConnectionStatus.DISCONNECTED }));
      setNeedsManualReconnect(prev => ({ ...prev, [agentType]: false }));
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.keys(socketRefs.current).forEach((key) => {
        const agentType = key as ChatAgentType;
        const socket = socketRefs.current[agentType];
        if (socket) {
          socket.onclose = () => { };
          socket.onerror = () => { };
          socket.onmessage = () => { }; // Prevent obsolete messages
          socket.close();
        }
        if (reconnectTimeoutRefs.current[agentType]) {
          clearTimeout(reconnectTimeoutRefs.current[agentType]!);
        }
      });
    };
  }, []);

  // Initialize pending messages queue
  useEffect(() => {
    Object.values(ChatAgentType).forEach(agentType => {
      pendingMessagesRef.current[agentType] = [];
    });
  }, []);

  const clearQueue = useCallback((agentType: ChatAgentType) => {
    if (pendingMessagesRef.current[agentType]) {
      pendingMessagesRef.current[agentType] = [];
      console.log(`[Socket] Queue cleared for ${agentType}`);
    }
  }, []);

  return {
    sockets,
    conversationIds,
    isFirstConnections,
    connectionStatuses,
    needsManualReconnect,
    connectWebSocket,
    manualReconnect,
    sendMessage,
    sendForwardMessage: sendForwardMessageToSocket,
    setConversationId,
    disconnectOtherSockets,
    disconnectWebSocket,
    clearQueue,
    setUserLocation,
  };
};
